import { HttpClient } from '@opallabs/api'

const BASE = '/integrations/sharepoint'

// Rails answers 412 when the current user hasn't connected a Microsoft
// account; callers gate on this to show the connect flow.
export const isNotConnectedError = (error: unknown): boolean =>
  (error as { response?: { status?: number } })?.response?.status === 412

// Graph's 403 passes straight through: the viewer is connected but SharePoint
// doesn't let them open this file.
export const isForbiddenError = (error: unknown): boolean =>
  (error as { response?: { status?: number } })?.response?.status === 403

export const connectPath = `${BASE}/connect`

// Whether the current user has a working Microsoft connection. Only a 412 is
// a clear "no"; anything else (404 unprovisioned, transient errors) reads as
// connected so the paste form still shows and import reports the real problem.
export const fetchConnectionStatus = (): Promise<boolean> =>
  HttpClient()
    .get(`${BASE}/accounts`)
    .then(() => true)
    .catch((error) => !isNotConnectedError(error))

export type SharepointDocument = {
  name: string
  webUrl: string
  mimeType: string
  size: string
  driveId: string
  itemId: string
  etag: string
}

export type SharepointImport = {
  document: SharepointDocument
}

export type SharepointRefresh = SharepointImport & { changed: boolean }

// Identifies the document by its stable Graph ids when the post has them,
// falling back to the stored web URL for documents imported before the ids
// were stored.
export type SharepointItemRef = {
  driveId?: string | null
  itemId?: string | null
  url?: string | null
}

type DocumentResponse = {
  name: string | null
  web_url: string | null
  mime_type: string | null
  size: number | null
  drive_id: string | null
  item_id: string | null
  etag: string | null
}

type ImportResponse = {
  document: DocumentResponse
}

type RefreshResponse = ImportResponse & { changed: boolean }

const toDocument = (document: DocumentResponse): SharepointDocument => ({
  name: document.name ?? '',
  webUrl: document.web_url ?? '',
  mimeType: document.mime_type ?? '',
  size: document.size == null ? '' : String(document.size),
  driveId: document.drive_id ?? '',
  itemId: document.item_id ?? '',
  etag: document.etag ?? '',
})

const itemRefParams = (ref: SharepointItemRef): Record<string, string> =>
  ref.driveId && ref.itemId
    ? { drive_id: ref.driveId, item_id: ref.itemId }
    : { url: ref.url ?? '' }

export const importSharepointDocument = async (
  url: string,
  postId: string | number
): Promise<SharepointImport> => {
  const response = await HttpClient().post<ImportResponse>(`${BASE}/import`, {
    url,
    post_id: postId,
  })
  return { document: toDocument(response.data.document) }
}

// The preview URL renders as the current viewer and expires within minutes,
// so it's never stored or shared — but grid and calendar views mount many
// cards at once (and remount on scroll), and each request can trigger a full
// OAuth refresh server-side, so the in-flight/recent request is cached per
// document. Failures are evicted so a viewer who connects can retry.
const PREVIEW_CACHE_TTL_MS = 5 * 60 * 1000
const previewCache = new Map<
  string,
  { promise: Promise<string>; expiresAt: number }
>()

export const fetchPreviewUrl = (ref: SharepointItemRef): Promise<string> => {
  const key = `${ref.driveId ?? ''}/${ref.itemId ?? ''}/${ref.url ?? ''}`
  const cached = previewCache.get(key)
  if (cached && cached.expiresAt > Date.now()) {
    return cached.promise
  }

  const promise = HttpClient()
    .post<{ get_url: string | null }>(`${BASE}/preview`, itemRefParams(ref))
    .then((response) => response.data.get_url ?? '')
  promise.catch(() => previewCache.delete(key))
  previewCache.set(key, {
    promise,
    expiresAt: Date.now() + PREVIEW_CACHE_TTL_MS,
  })
  return promise
}

// Re-resolves the document; `changed` is false while the file's eTag still
// matches the one the card stored.
export const refreshSharepointDocument = async (
  postId: string | number,
  ref: SharepointItemRef,
  etag?: string | null
): Promise<SharepointRefresh> => {
  const response = await HttpClient().post<RefreshResponse>(`${BASE}/refresh`, {
    post_id: postId,
    etag: etag || undefined,
    ...itemRefParams(ref),
  })
  const { document, changed } = response.data

  return { document: toDocument(document), changed }
}

export const importErrorMessage = (error: unknown): string =>
  (error as { response?: { data?: { error?: string } } })?.response?.data
    ?.error ?? 'Something went wrong importing that SharePoint document.'
